import type { LinkPreviewData } from './types.js';
import { parseVideoEmbedUrl } from './video-embed.js';

export function domainFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function titleFromUrlPath(url: string): string | undefined {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (!last || last.length < 2) return undefined;
    if (/^[a-f0-9-]{16,}$/i.test(last)) return undefined;

    const decoded = decodeURIComponent(last).replace(/[-_]+/g, ' ');
    if (!decoded.trim()) return undefined;
    return decoded.charAt(0).toUpperCase() + decoded.slice(1);
  } catch {
    return undefined;
  }
}

/** Build a link preview card from a bare URL (no OG fetch — backend metadata can override via `linkPreviews`). */
export function createLinkPreviewFromUrl(url: string): LinkPreviewData {
  const embed = parseVideoEmbedUrl(url);
  if (embed) {
    return {
      url: embed.watchUrl,
      title: embed.provider === 'youtube' ? 'YouTube' : 'Vimeo',
      domain: domainFromUrl(embed.watchUrl),
      embedUrl: embed.embedUrl,
      provider: embed.provider,
      imageUrl:
        embed.provider === 'youtube'
          ? `https://img.youtube.com/vi/${embed.videoId}/hqdefault.jpg`
          : undefined,
    };
  }

  const domain = domainFromUrl(url);
  return {
    url,
    domain,
    title: titleFromUrlPath(url) ?? domain,
  };
}

function normalizePreviewUrl(url: string): string {
  return url.trim().replace(/\/$/, '');
}

/** Merge server-provided preview fields over client-built defaults (matched by URL). */
export function mergeLinkPreviewsForUrls(
  urls: string[],
  existing?: LinkPreviewData[],
): LinkPreviewData[] {
  const byUrl = new Map(
    (existing ?? []).map((preview) => [normalizePreviewUrl(preview.url), preview]),
  );

  return urls.map((url) => {
    const built = createLinkPreviewFromUrl(url);
    const fromServer = byUrl.get(normalizePreviewUrl(url));
    if (!fromServer) return built;
    return {
      ...built,
      ...fromServer,
      url: fromServer.url || url,
    };
  });
}

/** Caption text with URL substrings removed (when URLs render as preview cards). */
export function captionWithoutUrls(
  text: string,
  urls: string[],
): string | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;

  let result = trimmed;
  for (const url of urls) {
    result = result.split(url).join(' ');
  }
  result = result.replace(/\s+/g, ' ').trim();
  return result || undefined;
}
